import mixpanel, { Dict } from 'mixpanel-browser';
import { webStorageUtil } from 'state/webStorage.state.service';
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import DeviceDetector from 'device-detector-js';
import {
  EFeatureFlag,
  ELocalStorageKeys,
  EPlatform,
  EStorePhase,
} from '../constants/enums';
import { EventSharedProps } from 'constants/apiRequest.types';
import { useMemo, useState } from 'react';
import { getAppVersion } from 'utils';
import { appStateService } from 'state/app.state.service';
import { getEnvVariable } from '@appcharge/shared-ui';

export interface UseCustomEventsReturn {
  sendCustomEvent: (eventType: string, options: Dict, eventPhase?: EStorePhase) => void;
  getEventsSharedProps: () => EventSharedProps;
  setDistinctId: () => void;
  resetSession: () => void;
}

interface EventSharedPropsState {
  platform: string;
  deviceManufacturer?: string;
  deviceModel?: string;
  os?: string;
  osVersion?: string;
  browser?: string;
  browserVersion?: string;
  storeVersion?: string;
  sharedUiVersion?: string;
  checkoutSdkVersion?: string;
  appVersion?: string;
}

export default function useCustomEvents(): UseCustomEventsReturn {
  const { i18n } = useTranslation();
  const publisherMetaData = webStorageUtil.get(ELocalStorageKeys.PUBLISHER_META);
  const [sharedProps] = useState<EventSharedPropsState>(() => {
    const deviceDetector = new DeviceDetector();
    const userAgent = navigator.userAgent;
    const deviceInfo = deviceDetector.parse(userAgent);

    return {
      platform: isMobile ? EPlatform.MOBILE : isTablet ? EPlatform.TABLET : EPlatform.DESKTOP,
      deviceManufacturer: deviceInfo.device?.brand,
      deviceModel: deviceInfo.device?.model,
      os: deviceInfo.os?.name,
      osVersion: deviceInfo.os?.version,
      browser: deviceInfo.client?.name,
      browserVersion: deviceInfo.client?.version,
      storeVersion: process.env.REACT_APP_STORE_VERSION,
      sharedUiVersion: process.env.REACT_APP_SHARED_UI_VERSION,
      checkoutSdkVersion: process.env.REACT_APP_CHECKOUT_SDK_VERSION,
      appVersion: getAppVersion(),
    };
  });

  const profileName = useMemo(() => {
    if (
      publisherMetaData?.integration?.isProfilesEnabled &&
      publisherMetaData?.featureFlags[EFeatureFlag.INTEGRATION_PROFILES]
    )
      return webStorageUtil.get(ELocalStorageKeys.INTEGRATION_PORFILE);
    return '';
  }, [
    publisherMetaData?.integration?.isProfilesEnabled,
    publisherMetaData?.featureFlags[EFeatureFlag.INTEGRATION_PROFILES],
  ]);

  const getPlatform = (): string => {
    return isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  };

  const getEventsSharedProps = (): EventSharedProps => {
    const timestamp = new Date().getTime();
    const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
    const sessionId = webStorageUtil.getStorageWithFallback()?.id;
    const platform = `${sharedProps.platform.toUpperCase()}${profileName ? `::${profileName}` : ''}`;

    return {
      ...sharedProps,
      sessionId,
      timestamp,
      playerId: playerData?.playerId,
      language: i18n.language,
      platform,
    };
  };

  const enrichOptions = (
    options: Dict,
    utmParams: Record<string, string>,
    platform: string
  ): Dict => {
    return {
      ...options,
      environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'local',
      publisher_id: publisherMetaData.storeTheme.publisherId,
      URL: window.location.origin,
      platform,
      language: i18n.language,
      store_version: process.env.REACT_APP_STORE_VERSION,
      shared_ui_version: process.env.REACT_APP_SHARED_UI_VERSION,
      checkout_sdk_version: process.env.REACT_APP_CHECKOUT_SDK_VERSION,
      utmSource: utmParams.utm_source || null,
      utmMedium: utmParams.utm_medium || null,
      utmCampaign: utmParams.utm_campaign || null,
      utmContent: utmParams.utm_content || null,
      utmTerm: utmParams.utm_term || null,
    };
  };

  const sendCustomEvent = (
    eventType: string,
    options: Dict,
    eventPhase: EStorePhase = EStorePhase.OTHER
  ): void => {

    if (typeof mixpanel === 'undefined' || !mixpanel.track) {
      console.warn('Mixpanel not available or analytics not consented, skipping event:', eventType);
      return;
    }

    const platform = getPlatform();
    const utmParams = appStateService.utms.get() || {};
    const enrichedOptions = enrichOptions(options, utmParams, platform);

    if (eventPhase === EStorePhase.POST_LOGIN) {
      const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
      const sessionData = webStorageUtil.getStorageWithFallback();

      if (playerData) {
        enrichedOptions.player_id = playerData.playerId;
        enrichedOptions.player_country = playerData.playerCountry;
      }

      if (sessionData?.id) {
        enrichedOptions.session_id = sessionData.id;
      }
    }

    try {
      // if (enrichedOptions.environment === 'production') {
      mixpanel.track(eventType, enrichedOptions);
      // }
    } catch (error) {
      console.warn('Error tracking Mixpanel event:', error);
    }
  };

  const setDistinctId = () => {
    if (typeof mixpanel === 'undefined' || !mixpanel.identify) {
      console.warn('Mixpanel not available or analytics not consented, skipping identify');
      return;
    }

    try {
      const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
      mixpanel.identify(`${publisherMetaData?.storeTheme?.publisherId}_${playerData?.playerId}`);
    } catch (error) {
      console.warn('Error setting Mixpanel distinct ID:', error);
    }
  };

  const resetSession = () => {
    if (typeof mixpanel === 'undefined' || !mixpanel.reset) {
      console.warn('Mixpanel not available, skipping reset');
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      console.warn('Error resetting Mixpanel session:', error);
    }
  };

  return {
    sendCustomEvent,
    setDistinctId,
    resetSession,
    getEventsSharedProps,
  };
}
